import { useState, useCallback, useRef, useEffect } from 'react';
import SearchSelect from './Select';

const SearchZip = ({
  value,
  onChange,
  inputRef,
  api,
  fetchData,
  terminalCode,
  OptionRenderer,
  fieldName,
  openMenuOnFocus,
  placeholder,
  reactContainer,
  errorMessage
}) => {
  // Simple helper to get string from value (handles objects)
  const getStringValue = (val) => {
    if (!val) return '';
    if (typeof val === 'object') {
      return val[fieldName] || val.zip || val.label || '';
    }
    return String(val);
  };

  const [selectedValue, setSelectedValue] = useState({
    label: getStringValue(value) || null,
    value: value
  });
  const [inputValue, setInputValue] = useState(getStringValue(value));
  const [hasError, setHasError] = useState(false);

  const cancelRequestRef = useRef(null);

  // Update state when value prop changes
  useEffect(() => {
    const stringVal = getStringValue(value);
    setSelectedValue({
      label: stringVal || null,
      value: value
    });
    setInputValue(stringVal);
  }, [value, fieldName]);

  const updateParent = useCallback(
    (newValue) => {
      if (onChange) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const handleChange = useCallback(
    (newValue) => {
      setSelectedValue(newValue);
      if (newValue) {
        api.suppressTab = false;
      }
      updateParent(newValue);
    },
    [api, updateParent]
  );

  const onInputChange = useCallback(
    (newInputValue) => {
      setInputValue(newInputValue);
      if (!newInputValue) {
        api.suppressTab = false;
      }
    },
    [api]
  );

  const getOptions = useCallback(
    (options, hasError = false) => {
      const isLoading = !options.length;
      api.suppressTab = isLoading;
      if (hasError) {
        setHasError(hasError);
        return;
      }
      if (!value) {
        handleChange(options[0]);
      }
    },
    [api, value, handleChange]
  );

  const initializeCancelToken = useCallback((c) => {
    cancelRequestRef.current = c;
  }, []);

  const fetchOptions = useCallback(
    (inputValue) => {
      setHasError(false);
      cancelRequestRef.current?.('Request canceled'); // Cancel ongoing request if any
      api.suppressTab = inputValue;
      return fetchData?.({
        inputValue,
        getOptions,
        initializeCancelToken,
        terminalCode
      });
    },
    [api, fetchData, terminalCode, getOptions, initializeCancelToken]
  );

  const handleKeyDown = useCallback(
    (event) => {
      const KEY_TAB = 9;
      const { keyCode } = event || {};
      if (keyCode === KEY_TAB && api.suppressTab) {
        event.preventDefault();
      }
    },
    [api]
  );

  return (
    <SearchSelect
      onChange={handleChange}
      onInputChange={onInputChange}
      inputValue={inputValue}
      value={selectedValue}
      getOptionLabel={(e) => {
        return e[fieldName] ?? e.label;
      }}
      getOptionValue={(e) => e[fieldName] ?? e.label}
      fetchOptions={fetchOptions}
      onKeyDown={handleKeyDown}
      OptionRenderer={OptionRenderer}
      inputRef={inputRef}
      openMenuOnFocus={openMenuOnFocus ?? false}
      placeholder={placeholder ?? 'Select...'}
      hasError={hasError}
      errorMessage={errorMessage}
      reactContainer={reactContainer}
    />
  );
};

export default SearchZip;
